<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { formErrorsFrom } from './formErrors';
import { toast } from 'vue-sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import ModelSearchPicker from '@/components/ModelSearchPicker.vue';
import ZoneSearchOption from '@/components/ZoneSearchOption.vue';
import CreateChoiceField from '@/components/createRecord/CreateChoiceField.vue';
import { modelIcon } from '@/composables/modelIcons';
import { recordPathFor } from '@/navigation/areas';
import type { CreateField } from '@/components/createRecord/createRecordForms';

/**
 * "New player" in Create New. A player that is plugged in is assigned on its
 * own to the internal new zone, so a new player is really a move: pick one of
 * the players waiting there, pick the zone it belongs in, and the backend does
 * the whole move the way phpapi does, including telling the device.
 *
 * Contract (docs/specs/macro-style-followups.md, item 17):
 * - `GET /api/location_zone_player/new-zone-players` lists the waiting players.
 * - `POST /api/location_zone_player/{id}/assign` with `{ location_zone_id,
 *   time_zone_id? }` moves one, answering the moved player, 422 or 403.
 */

export interface NewZonePlayer {
    id: number;
    display_string: string;
    model_type: string | null;
    lan: string | null;
    tags: string[];
    last_checkin_at: string | null;
    is_online: boolean;
    player_type: string | null;
}

const open = defineModel<boolean>('open', { required: true });

const router = useRouter();
const queryClient = useQueryClient();

const waiting = useQuery({
    queryKey: ['new-zone-players'],
    enabled: open,
    queryFn: async (): Promise<NewZonePlayer[]> => {
        const { default: http } = await import('@/services/http');
        const response = await http.get<{ data: NewZonePlayer[] }>('/api/location_zone_player/new-zone-players');
        return response.data?.data ?? [];
    },
});
/** Online players first, so the one just plugged in is easy to find. */
const players = computed(() => [...(waiting.data.value ?? [])].sort((first, second) => Number(second.is_online) - Number(first.is_online)));

const playerId = ref<number | null>(null);
const zoneId = ref<number | null>(null);
/** The chosen zone's search document, so the picker's button draws it like its options. */
const zoneRow = ref<Record<string, unknown> | null>(null);
const timeZoneId = ref<string | number | boolean | null>(null);
const fieldErrors = ref<Record<string, string>>({});
const formError = ref<string | null>(null);
const saving = ref(false);

const TIME_ZONE_FIELD: CreateField = { name: 'time_zone_id', label: 'Time zone', kind: 'choice', required: false, optionsSlug: 'time_zone' };

watch(open, (isOpen) => {
    if (isOpen) {
        playerId.value = null;
        zoneId.value = null;
        zoneRow.value = null;
        timeZoneId.value = null;
        fieldErrors.value = {};
        formError.value = null;
        // No refetch here: opening turns the query on, and that fetches a fresh list.
    }
});

const missing = computed(() => (playerId.value === null ? 'a player' : zoneId.value === null ? 'a zone' : null));

function lastSeen(player: NewZonePlayer): string {
    if (!player.last_checkin_at) return 'Never checked in';
    return `Last check-in ${new Date(player.last_checkin_at).toLocaleString()}`;
}

async function assign() {
    if (missing.value || saving.value || playerId.value === null) return;
    saving.value = true;
    fieldErrors.value = {};
    formError.value = null;
    try {
        const { default: http } = await import('@/services/http');
        const body: Record<string, number> = { location_zone_id: zoneId.value as number };
        if (timeZoneId.value !== null) body.time_zone_id = Number(timeZoneId.value);
        await http.post(`/api/location_zone_player/${playerId.value}/assign`, body);
        await queryClient.invalidateQueries({ queryKey: ['new-zone-players'] });
        await queryClient.invalidateQueries({ queryKey: ['search'] });
        const movedId = playerId.value;
        open.value = false;
        toast.success(`Player ${movedId} is moving to its zone. It resets to pick up the change.`);
        void router.push(recordPathFor('LocationZonePlayer', movedId));
    } catch (error) {
        ({ fieldErrors: fieldErrors.value, formError: formError.value } = formErrorsFrom(error, ['location_zone_id', 'time_zone_id'], {
            forbidden: "You don't have permission to move this player into that zone.",
            fallback: 'Could not move the player. Try again.',
        }));
    } finally {
        saving.value = false;
    }
}
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="sm:max-w-lg" data-testid="assign-player-dialog">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2.5">
          <component :is="modelIcon('LocationZonePlayer')" class="size-5" aria-hidden="true" />
          New player
        </DialogTitle>
        <DialogDescription>Pick a player that was just plugged in, and the zone it belongs in.</DialogDescription>
      </DialogHeader>

      <div class="flex flex-col gap-4">
        <fieldset class="flex flex-col gap-1.5">
          <legend class="mb-1.5 text-sm font-medium">Player waiting in the new zone</legend>
          <p v-if="waiting.isLoading.value" class="text-sm text-muted-foreground">Loading…</p>
          <p v-else-if="waiting.isError.value" class="text-sm text-destructive-foreground" role="alert">Couldn't load the waiting players.</p>
          <p v-else-if="players.length === 0" class="text-sm text-muted-foreground" data-testid="assign-player-none">
            No players are waiting. Plug one in and it shows here once it checks in.
          </p>
          <div v-else class="flex max-h-64 flex-col gap-1 overflow-y-auto" role="radiogroup" aria-label="Player">
            <label
              v-for="player in players"
              :key="player.id"
              class="flex cursor-pointer items-start gap-2.5 rounded-lg p-2 hover:bg-muted has-[:checked]:bg-muted"
              data-testid="assign-player-option"
            >
              <input v-model="playerId" type="radio" name="new-player" :value="player.id" class="mt-1" />
              <span class="flex min-w-0 flex-1 flex-col">
                <span class="flex items-center gap-2 text-sm font-medium">
                  <span
                    class="size-2 shrink-0 rounded-full"
                    :class="player.is_online ? 'bg-green-500' : 'bg-muted-foreground/40'"
                    :title="player.is_online ? 'Online' : 'Offline'"
                  />
                  <span class="truncate">{{ player.display_string }}</span>
                </span>
                <span class="text-xs text-muted-foreground">
                  {{ [player.model_type, player.player_type, player.lan, ...player.tags].filter(Boolean).join(' · ') || 'No details reported' }}
                </span>
                <span class="text-xs text-muted-foreground">{{ lastSeen(player) }}</span>
              </span>
            </label>
          </div>
        </fieldset>

        <div class="flex flex-col gap-1.5">
          <span class="text-sm font-medium">Zone</span>
          <ModelSearchPicker
            :model-value="zoneId === null ? null : String(zoneId)"
            slug="location_zone"
            test-id-prefix="assign-player-zone"
            @update:model-value="(id: string | null) => (zoneId = id === null ? null : Number(id))"
            @pick="(row: Record<string, unknown>) => (zoneRow = row)"
          >
            <template v-if="zoneRow" #selected="{ label }">
              <ZoneSearchOption :row="zoneRow" :label="label ?? ''" single-line />
            </template>
            <template #option="{ row, label }">
              <ZoneSearchOption :row="row" :label="label" />
            </template>
          </ModelSearchPicker>
          <p v-if="fieldErrors.location_zone_id" class="text-sm text-destructive-foreground" role="alert">{{ fieldErrors.location_zone_id }}</p>
        </div>

        <div class="flex flex-col gap-1.5">
          <label for="assign-player-time-zone" class="text-sm font-medium">
            Time zone<span class="font-normal text-muted-foreground"> (optional)</span>
          </label>
          <CreateChoiceField v-model="timeZoneId" :field="TIME_ZONE_FIELD" input-id="assign-player-time-zone" />
          <p v-if="fieldErrors.time_zone_id" class="text-sm text-destructive-foreground" role="alert">{{ fieldErrors.time_zone_id }}</p>
          <p v-else class="text-xs text-muted-foreground">Left empty, the player takes the zone's time zone.</p>
        </div>

        <p v-if="formError" class="text-sm text-destructive-foreground" role="alert" data-testid="assign-player-error">{{ formError }}</p>
      </div>

      <DialogFooter>
        <Button variant="outline" @click="open = false">Cancel</Button>
        <Button
          variant="accent"
          :disabled="Boolean(missing) || saving"
          :title="missing ? `Pick ${missing} first.` : undefined"
          data-testid="assign-player-submit"
          @click="assign"
        >
          {{ saving ? 'Moving…' : 'Move into zone' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
